import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { RekognitionClient, CompareFacesCommand } from '@aws-sdk/client-rekognition';
import { AWS_REKOGNITION_CONFIG } from 'config';
import { SUCCESS_THRESHOLD } from 'store/faceMatchSlice/faceMatchSlice';

let rekognitionClient = null;

const getClient = () => {
    if (!rekognitionClient) {
        const cognitoIdentityClient = new CognitoIdentityClient({ region: AWS_REKOGNITION_CONFIG.REGION });

        rekognitionClient = new RekognitionClient({
            region: AWS_REKOGNITION_CONFIG.REGION,
            credentials: fromCognitoIdentityPool({
                client: cognitoIdentityClient,
                identityPoolId: AWS_REKOGNITION_CONFIG.IDENTITY_POOL_ID,
            }),
        });
    }
    return rekognitionClient;
};

const MAX_IMAGE_SIZE_BYTES = 4 * 1024 * 1024;

const compressImageIfNeeded = async (base64, maxSizeBytes = MAX_IMAGE_SIZE_BYTES) => {
    let cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
    cleanBase64 = cleanBase64.replace(/\s/g, '');

    const currentSizeBytes = Math.ceil((cleanBase64.length * 3) / 4);

    if (currentSizeBytes <= maxSizeBytes) {
        return base64;
    }

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            let { width, height } = img;
            const maxDimension = 1920;

            if (width > maxDimension || height > maxDimension) {
                const ratio = Math.min(maxDimension / width, maxDimension / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            let quality = 1.0;
            let compressedBase64 = canvas.toDataURL('image/jpeg', quality);
            let compressedSize = Math.ceil(((compressedBase64.split(',')[1] || '').length * 3) / 4);

            while (compressedSize > maxSizeBytes && quality > 0.5) {
                quality -= 0.05;
                compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                compressedSize = Math.ceil(((compressedBase64.split(',')[1] || '').length * 3) / 4);
            }

            resolve(compressedBase64);
        };
        img.onerror = (err) => {
            console.error('[FaceMatchingService] Failed to load image for compression:', err);
            reject(new Error('Failed to load image for compression'));
        };

        img.src = base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
    });
};

const base64ToUint8Array = (base64) => {
    let cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

    cleanBase64 = cleanBase64.replace(/\s/g, '');

    if (!cleanBase64 || cleanBase64.length === 0) {
        throw new Error('Empty base64 string provided');
    }

    const padding = cleanBase64.length % 4;
    if (padding > 0) {
        cleanBase64 += '='.repeat(4 - padding);
    }

    cleanBase64 = cleanBase64.replace(/-/g, '+').replace(/_/g, '/');

    try {
        const binaryString = atob(cleanBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return bytes;
    } catch (error) {
        console.error('[FaceMatchingService] Failed to decode base64:', error);
        throw new Error(`Invalid base64 string: ${error.message}`);
    }
};

export const compareFaces = async (sourceImageBase64, targetImageBase64, similarityThreshold = 0) => {
    const client = getClient();
    const compressedSource = await compressImageIfNeeded(sourceImageBase64);
    const compressedTarget = await compressImageIfNeeded(targetImageBase64);
    const sourceBytes = base64ToUint8Array(compressedSource);
    const targetBytes = base64ToUint8Array(compressedTarget);
    const params = {
        SourceImage: {
            Bytes: sourceBytes,
        },
        TargetImage: {
            Bytes: targetBytes,
        },
        SimilarityThreshold: similarityThreshold,
    };

    try {
        const command = new CompareFacesCommand(params);
        const response = await client.send(command);

        return response;
    } catch (error) {
        if (error.name === 'InvalidParameterException' || error.Code === 'InvalidParameterException') {
            const customError = new Error('No face detected. Ensure a clear, visible face in both images.');
            customError.code = 'NO_FACE_DETECTED';
            customError.originalError = error;
            throw customError;
        }

        if (error.name === 'ImageTooLargeException' || error.Code === 'ImageTooLargeException') {
            const customError = new Error('One or both images exceed the 5MB size limit. Please use smaller images.');
            customError.code = 'IMAGE_TOO_LARGE';
            customError.originalError = error;
            throw customError;
        }

        if (error.name === 'InvalidImageFormatException' || error.Code === 'InvalidImageFormatException') {
            const customError = new Error('Invalid image format. Please use JPEG or PNG images.');
            customError.code = 'INVALID_IMAGE_FORMAT';
            customError.originalError = error;
            throw customError;
        }

        throw error;
    }
};

export const extractSimilarity = (response) => {
    if (response?.FaceMatches && response.FaceMatches.length > 0) {
        return response.FaceMatches[0].Similarity;
    }
    return null;
};

export const isFaceMatchSuccessful = (response, minSimilarity = SUCCESS_THRESHOLD) => {
    const similarity = extractSimilarity(response);
    return similarity !== null && similarity >= minSimilarity;
};

export default {
    compareFaces,
    extractSimilarity,
    isFaceMatchSuccessful,
};
